import { formatMoney } from '../../lib/format-money.ts'

export { formatMoney }

export const TODAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Kyiv',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(new Date())

const dayFormatter = new Intl.DateTimeFormat('uk-UA', {
  day: 'numeric',
  month: 'short',
  timeZone: 'Europe/Kyiv',
})
const fullFormatter = new Intl.DateTimeFormat('uk-UA', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Europe/Kyiv',
})
const timeFormatter = new Intl.DateTimeFormat('uk-UA', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Kyiv',
})

const dayDiff = (isoDate: string): number => {
  const day = (value: string) => Date.parse(`${value.slice(0, 10)}T00:00:00Z`)
  return Math.round((day(isoDate) - day(TODAY)) / 86_400_000)
}

export const formatDay = (iso: string): string => dayFormatter.format(new Date(iso))
export const formatDate = (iso: string): string => fullFormatter.format(new Date(iso))
export const formatTime = (iso: string): string => timeFormatter.format(new Date(iso))

/** "Сьогодні", "Завтра", "Прострочено 2 дні" — the words people actually use for due dates. */
export const formatDue = (
  iso: string,
): { label: string; tone: 'critical' | 'warning' | 'muted' } => {
  const diff = dayDiff(iso)
  if (diff < 0) {
    const days = Math.abs(diff)
    return { label: `Прострочено ${days} ${pluralDays(days)}`, tone: 'critical' }
  }
  if (diff === 0) return { label: 'Сьогодні', tone: 'warning' }
  if (diff === 1) return { label: 'Завтра', tone: 'muted' }
  return { label: formatDay(iso), tone: 'muted' }
}

export const dueBucket = (iso: string | null): 'overdue' | 'today' | 'later' => {
  if (!iso) return 'later'
  const diff = dayDiff(iso)
  return diff < 0 ? 'overdue' : diff === 0 ? 'today' : 'later'
}

const pluralRules = new Intl.PluralRules('uk-UA')
const DAY_FORMS: Record<string, string> = { one: 'день', few: 'дні', many: 'днів', other: 'дня' }
export const pluralDays = (count: number): string => DAY_FORMS[pluralRules.select(count)] ?? 'днів'

/** Picks the Ukrainian word form for a count: forms are [one, few, many], e.g. ['завдання', 'завдання', 'завдань']. */
export const plural = (count: number, forms: readonly [string, string, string]): string => {
  const rule = pluralRules.select(count)
  return rule === 'one' ? forms[0] : rule === 'few' ? forms[1] : forms[2]
}

const groupThousands = (value: string): string => value.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

/** Quantity in Ukrainian notation: grouped thousands, comma decimal, trailing zeros dropped. */
export const formatQuantity = (value: string, unit?: string): string => {
  const negative = value.startsWith('-')
  const [whole = '0', fraction = ''] = (negative ? value.slice(1) : value).split('.')
  const trimmed = fraction.replace(/0+$/, '')
  const body = `${groupThousands(whole)}${trimmed ? `,${trimmed}` : ''}`
  return `${negative ? '−' : ''}${body}${unit ? ` ${unit}` : ''}`
}

const countFormatter = new Intl.NumberFormat('uk-UA')
export const formatCount = (value: number): string => countFormatter.format(value)
